import React, { useState, useEffect, useCallback } from 'react';
import { withDevGuard } from '../../components/common/DevOnlyGuard';
import { devFilesApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { RefreshCw, Plus, X, Bot } from 'lucide-react';
import { formatTimestamp, generatedStamp } from '../../utils/dateTime';

const INDEX_FILE = 'PMD-Claude-Notes.md';

/**
 * ClaudeNotesPage - Development page to view the Claude notes set:
 * PMD-Claude-Notes.md (index) + documents/claude/notes/*.md topic files.
 * Notes hold current-state rules only; sizes are budgeted (over-budget files flag red).
 * TODO: Delete this file before production deployment
 */
const ClaudeNotesPage = () => {
  const [content, setContent] = useState('');
  const [lastModified, setLastModified] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [filePath, setFilePath] = useState(''); // Kept for future use
  const [files, setFiles] = useState([]);
  const [activeFile, setActiveFile] = useState(INDEX_FILE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNote, setNewNote] = useState('');

  const { notify } = useNotifications();

  const loadFiles = useCallback(async () => {
    try {
      const response = await devFilesApi.getClaudeNotesFiles();
      setFiles(response.data || []);
    } catch (err) {
      // Older backend without the files endpoint — index-only mode
      setFiles([]);
    }
  }, []);

  const loadContent = useCallback(async (name) => {
    const target = name || INDEX_FILE;
    try {
      setLoading(true);
      const response = target === INDEX_FILE
        ? await devFilesApi.getClaudeNotes()
        : await devFilesApi.getClaudeNotesFile(target);
      setContent(response.data.content);
      setLastModified(new Date(response.data.lastModified));
      setFilePath(response.data.filePath);
      setActiveFile(target);
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to load file');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadFiles();
    loadContent(INDEX_FILE);
  }, [loadFiles, loadContent]);

  const addNoteToContent = (note, indexContent) => {
    // Find the "## Notes" section and add the note there
    const lines = indexContent.split('\n');
    const newLines = [];
    let foundNotesSection = false;
    let insertedNote = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      newLines.push(line);

      // Look for the Notes section
      if (line.startsWith('## Notes')) {
        foundNotesSection = true;
      }
      // After finding Notes section, look for the placeholder or add after empty line
      else if (foundNotesSection && !insertedNote) {
        if (line.includes('*(Add context') || line.trim() === '') {
          // Add note with timestamp
          const timestamp = generatedStamp();
          newLines.push(`- **[${timestamp}]** ${note}`);
          insertedNote = true;
          // Skip the placeholder if that's what we found
          if (line.includes('*(Add context')) {
            continue;
          }
        }
      }
    }

    // If we never found the Notes section, append it
    if (!insertedNote) {
      newLines.push('');
      newLines.push('## Notes');
      const timestamp = generatedStamp();
      newLines.push(`- **[${timestamp}]** ${note}`);
    }

    return newLines.join('\n');
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    try {
      setSaving(true);
      // error cleared

      // Notes always land in the index's Inbox section; Claude triages them into topic files.
      // Fetch the index fresh — the viewer may be on a topic file.
      const indexResponse = await devFilesApi.getClaudeNotes();
      const updatedContent = addNoteToContent(newNote, indexResponse.data.content);
      await devFilesApi.updateClaudeNotes(updatedContent);

      setNewNote('');
      setShowAddNote(false);
      await loadFiles();
      await loadContent(INDEX_FILE);
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  // Simple markdown to HTML converter for display
  const renderMarkdown = (text) => {
    if (!text) return null;

    const lines = text.split('\n');
    const elements = [];
    let inTable = false;
    let tableRows = [];
    let listItems = [];
    let listType = null;

    const processInline = (str) => {
      str = str.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      str = str.replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, '<em>$1</em>');
      str = str.replace(/`(.+?)`/g, '<code>$1</code>');
      str = str.replace(/~~(.+?)~~/g, '<del>$1</del>');
      return str;
    };

    const flushList = () => {
      if (listItems.length > 0) {
        if (listType === 'ordered') {
          elements.push(
            <ol key={`list-${elements.length}`} className="sprint-ordered-list">
              {listItems.map((item, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: processInline(item) }} />
              ))}
            </ol>
          );
        } else {
          elements.push(
            <ul key={`list-${elements.length}`} className="sprint-unordered-list">
              {listItems.map((item, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: processInline(item) }} />
              ))}
            </ul>
          );
        }
        listItems = [];
        listType = null;
      }
    };

    const flushTable = () => {
      if (tableRows.length > 0) {
        elements.push(
          <table key={`table-${elements.length}`} className="sprint-table">
            <thead>
              <tr>
                {tableRows[0].map((cell, i) => <th key={i}>{cell}</th>)}
              </tr>
            </thead>
            <tbody>
              {tableRows.slice(2).map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <td key={j} dangerouslySetInnerHTML={{ __html: processInline(cell) }} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        );
        tableRows = [];
      }
    };

    lines.forEach((line, idx) => {
      if (line.startsWith('|')) {
        flushList();
        inTable = true;
        const cells = line.split('|').filter(c => c.trim() !== '').map(c => c.trim());
        tableRows.push(cells);
        return;
      } else if (inTable) {
        flushTable();
        inTable = false;
      }

      if (line.startsWith('# ')) {
        flushList();
        elements.push(<h1 key={idx}>{line.slice(2)}</h1>);
      } else if (line.startsWith('## ')) {
        flushList();
        elements.push(<h2 key={idx}>{line.slice(3)}</h2>);
      } else if (line.startsWith('### ')) {
        flushList();
        elements.push(<h3 key={idx}>{line.slice(4)}</h3>);
      } else if (line.startsWith('---')) {
        flushList();
        elements.push(<hr key={idx} />);
      } else if (line.match(/^\d+\.\s/)) {
        if (listType !== 'ordered') {
          flushList();
          listType = 'ordered';
        }
        const match = line.match(/^\d+\.\s*(.*)$/);
        if (match) {
          listItems.push(match[1]);
        }
      } else if (line.startsWith('- ')) {
        if (listType !== 'unordered') {
          flushList();
          listType = 'unordered';
        }
        listItems.push(line.slice(2));
      } else if (line.startsWith('*(') && line.endsWith(')*')) {
        flushList();
        elements.push(
          <p key={idx} className="sprint-note">
            <em>{line.slice(1, -1)}</em>
          </p>
        );
      } else if (line.trim()) {
        flushList();
        elements.push(
          <p key={idx} dangerouslySetInnerHTML={{ __html: processInline(line) }} />
        );
      } else {
        flushList();
        elements.push(<div key={idx} className="sprint-spacer" />);
      }
    });

    flushList();
    if (inTable) flushTable();

    return elements;
  };

  if (loading) {
    return <div className="dev-page loading">Loading...</div>;
  }

  return (
    <div className="dev-module claude-notes-page">
      {/* Header - uses unified dev-module styling */}
      <div className="dev-module-header">
        <div className="dev-module-header-left">
          <h1>
            <Bot size={20} className="stui-imgupload-title-icon" />
            PMD Claude Notes
          </h1>
          <span className="dev-module-meta">
            {formatTimestamp(lastModified)}
          </span>
        </div>
        <div className="dev-module-header-right">
          {/* CRUD buttons */}
          <button
            className="crud-btn"
            onClick={() => setShowAddNote(true)}
            title="Add Note"
          >
            <Plus size={18} />
          </button>
          <button
            className="crud-btn"
            onClick={() => { loadFiles(); loadContent(activeFile); }}
            disabled={loading}
            title="Refresh"
          >
            <RefreshCw size={18} className={loading ? 'stui-spinning' : ''} />
          </button>
        </div>
      </div>

      {/* Notes file tabs — STUI ssg tab bar; size badge flags red when a file blows its budget */}
      {files.length > 0 && (
        <div className="stui-ssg-tab-bar">
          {files.map((f) => {
            const overBudget = f.sizeBytes > f.budgetBytes;
            return (
              <button
                key={f.name}
                className={`stui-ssg-tab-btn${f.name === activeFile ? ' active' : ''}`}
                onClick={() => loadContent(f.name)}
                title={`${f.name} — ${(f.sizeBytes / 1024).toFixed(1)} KB of ${(f.budgetBytes / 1024).toFixed(0)} KB budget${overBudget ? ' — OVER BUDGET, condense it' : ''}`}
              >
                {f.label}
                <span style={overBudget ? { color: 'var(--color-error, #c0392b)', fontWeight: 600 } : undefined}>
                  {' '}· {(f.sizeBytes / 1024).toFixed(1)}KB{overBudget ? ' ⚠' : ''}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Add Note Modal */}
      {showAddNote && (
        <div className="stui-modal-overlay">
          <div className="stui-modal stui-modal-md">
            <div className="stui-modal-header">
              <h3>Add Note</h3>
              <button type="button" className="stui-modal-close" onClick={() => setShowAddNote(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="stui-modal-body">
              <div className="form-group">
                <label>Note</label>
                <textarea
                  className="form-input"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add context, decisions, or anything Claude needs to know..."
                  rows={4}
                  autoFocus
                />
              </div>
            </div>
            <div className="stui-modal-footer">
              <button
                className="stui-btn stui-btn-secondary"
                onClick={() => setShowAddNote(false)}
              >
                Cancel
              </button>
              <button
                className="stui-btn stui-btn-primary"
                onClick={handleAddNote}
                disabled={saving || !newNote.trim()}
              >
                {saving ? 'Adding...' : 'Add Note'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content area - full width for markdown display */}
      <div className="dev-module-content-full">
        <div className="sprint-display">
          {renderMarkdown(content)}
        </div>
      </div>
    </div>
  );
};

export default withDevGuard(ClaudeNotesPage, 'Claude Notes');
